import React from 'react';

import { Alert, Descriptions, Flex, Modal, Space } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';

import type {
  CounselorMeritBadge,
  MeritBadge,
  MeritBadgeCounselorProfile,
  RosterYouthMember,
} from '@appTypes/esb';
import { Img } from '@shared/components';
import { getPreferredName } from '@shared/utils/preferredName';

export interface ErrorItem {
  youthMemberId: number;
  message: string;
}

interface ConfirmationModalProps {
  open: boolean;
  selectedYouth: RosterYouthMember[];
  mbcProfle: MeritBadgeCounselorProfile;
  counselorMB: CounselorMeritBadge;
  meritBadge: MeritBadge;
  isLoading: boolean;
  errors: ErrorItem[];
  onCancel: () => void;
  onSubmit: () => void;
  onClearErrors: () => void;
}

export default function ConfirmationModal(props: ConfirmationModalProps) {
  const counselorName = getPreferredName(props.mbcProfle);
  const mbImage = props.meritBadge.imageUrl100;
  const mbName = props.meritBadge.name;

  if (!props.meritBadge) return null;

  return (
    <Modal
      open={props.open}
      destroyOnClose
      title={<FormattedMessage id="shared.confirm" />}
      width={800}
      onClose={props.onCancel}
      onCancel={props.onCancel}
      onOk={props.onSubmit}
      okText={<FormattedMessage id="shared.confirm" />}
      okButtonProps={{ loading: props.isLoading }}
      cancelButtonProps={{ type: 'default', disabled: props.isLoading }}
    >
      <Flex vertical gap={'small'}>
        {props.errors.length > 0 && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={props.onClearErrors}
            message={
              <FormattedMessage id="page.mbcBulkAssign.modal.errorTitle" />
            }
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {props.errors.map(error => (
                  <li key={error.youthMemberId}>
                    <FormattedMessage
                      id="page.mbcBulkAssign.modal.errorItem"
                      values={{
                        memberId: error.youthMemberId,
                        message: error.message,
                      }}
                    />
                  </li>
                ))}
              </ul>
            }
          />
        )}
        <Flex vertical>
          <Descriptions
            bordered
            layout="vertical"
            size="small"
            items={[
              {
                key: 'mb',
                label: (
                  <FormattedMessage id="page.mbcBulkAssign.labels.meritBadge" />
                ),
                children: (
                  <Space>
                    <Img src={mbImage} height={32} alt="Merit Badge" />
                    <Text>{mbName}</Text>
                  </Space>
                ),
              },
              {
                key: 'name',
                label: (
                  <FormattedMessage id="page.mbcBulkAssign.labels.counselorName" />
                ),
                children: counselorName,
              },
            ]}
          />
        </Flex>
        <div>
          <Text>
            <FormattedMessage id="page.mbcBulkAssign.modal.description" />:
          </Text>
          <ul>
            {props.selectedYouth.map(item => (
              <li key={item.userId}>
                <Text>{getPreferredName(item)}</Text>
              </li>
            ))}
          </ul>
        </div>
      </Flex>
    </Modal>
  );
}
